/**
 * PTAX ÚNICA da proposta (F1-57, aprovada em 09/10/2026), em "Condições comerciais e frete":
 *  - uma taxa para TODOS os itens em dólar da versão; a PTAX de cada cotação fica só como histórico (P4);
 *  - alterar mostra a PRÉVIA do efeito e aplica com uma confirmação: item pela margem recalculado (P1), preço fixo
 *    mantido com a margem efetiva atualizada (P2), itens em reais intactos;
 *  - manual exige a permissão "editar PTAX", origem, data de referência e justificativa (auditada ao salvar);
 *  - proposta do modelo anterior com taxas divergentes: escolha EXPLÍCITA antes do envio, nada automático (P3);
 *  - versão emitida: somente leitura (a PTAX fica congelada na versão).
 */
import { useState, type ReactNode } from 'react';
import { api } from '../../api.ts';
import { useSessao } from '../../sessao.tsx';
import { Ajuda, Modal } from '../ui.tsx';
import { CampoData } from '../CampoData.tsx';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import {
  ORIGENS_PTAX,
  aplicarPtaxDaProposta,
  novaPtax,
  previaPtaxDaProposta,
  ptaxDaProposta,
  ptaxesDosItens,
  substituirPtax,
  temItemEmDolar,
  usaPtaxDaProposta,
  validarPtaxManual,
  type OrigemPtaxManual,
  type PtaxCotacao,
  type PtaxManual,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { useProposta } from './contexto.tsx';
import { moeda2, pct, pctNum } from './formatos.ts';

const ptax4 = (v?: number) => (v === undefined ? 'a definir' : v.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }));
const agora = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 16).replace(' ', 'T');
interface RespostaPtax {
  disponivel: boolean;
  oficial: NonNullable<PtaxCotacao['oficial']>;
  aviso?: string;
  motivo: string;
}

/** Proposta: aplica uma nova PTAX à versão inteira (o servidor reaplica e confere ao salvar). */
type Aplicar = (nova: Versao) => void;

export function PtaxDaProposta({ v, editavel, aplicar, aoHistorico, extra }: { v: Versao; editavel: boolean; aplicar: Aplicar; /** P2-30: abre o Histórico da PTAX no painel único de consultas */ aoHistorico?: () => void; /** P2-27: ação extra na faixa (Recalcular pela margem desejada) */ extra?: ReactNode }) {
  const { d, eu } = useProposta();
  const { pode } = useSessao();
  const [alterar, setAlterar] = useState<null | { valor?: number; oficial?: RespostaPtax['oficial']; escolhida?: number }>(null);
  const [historico, setHistorico] = useState(false);
  const [aviso, setAviso] = useState('');
  if (!temItemEmDolar(v, d) && !usaPtaxDaProposta(v)) return null;
  const podeManual = editavel && pode('editar_ptax', 'proposta');
  const p = v.ptaxProposta;
  const valor = ptaxDaProposta(v);
  const divergentes = !usaPtaxDaProposta(v) ? ptaxesDosItens(v, d) : [];
  const consultar = async () => {
    setAviso('Consultando a PTAX oficial…');
    try {
      const r = await api.get<RespostaPtax>('/api/ptax');
      if (!r.disponivel) return setAviso(r.motivo);
      setAviso(r.aviso ?? '');
      setAlterar({ oficial: r.oficial, valor: r.oficial.valor });
    } catch (e) {
      setAviso(`Não foi possível consultar: ${(e as Error).message}`);
    }
  };
  const meta = !p
    ? divergentes.length > 1
      ? 'os itens em dólar usam PTAX diferentes'
      : 'versão no modelo anterior: PTAX de cada cotação (preservada)'
    : p.manual
      ? `Manual · ${p.manual.origem} · referência ${dataBr(p.manual.dataReferencia)}`
      : p.oficial
        ? `Oficial · Banco Central do Brasil · venda, fechamento de ${dataBr(p.oficial.dataReferencia)}`
        : 'consulte a PTAX oficial ou informe a manual';
  return (
    <section className="ptax-proposta faixa-ptax" data-teste="ptax-proposta" aria-label="PTAX da proposta">
      <div className="ptax-proposta-caixa">
        <span className="rotulo-faixa-ptax">PTAX da proposta (USD)</span>
        <Ajuda titulo="PTAX da proposta">Uma taxa para todos os itens em dólar da versão: consulte a oficial (Banco Central, venda, fechamento) ou informe a manual com a permissão “editar PTAX”, origem, data e justificativa. Alterar mostra a prévia antes de aplicar; item pela margem é recalculado e preço fixo é mantido. Fica congelada na versão enviada.</Ajuda>
        <strong className="ptax-proposta-valor" data-teste="ptax-proposta-valor">
          {usaPtaxDaProposta(v) ? ptax4(valor) : divergentes.length > 1 ? 'a escolher' : ptax4(divergentes[0])}
        </strong>
        <span className="texto-secundario">{meta}</span>
        <span className="espaco" />
        {editavel && (
          <button type="button" className="botao-secundario pequeno" onClick={() => void consultar()} data-teste="consultar-ptax-proposta">
            Consultar PTAX oficial
          </button>
        )}
        {podeManual && (
          <button type="button" className="botao-secundario pequeno" onClick={() => setAlterar({ valor: valor })} data-teste="alterar-ptax-proposta">
            Alterar PTAX 🔒
          </button>
        )}
        {p && p.historico.length > 0 && (
          <button type="button" className="botao-secundario pequeno" onClick={() => (aoHistorico ? aoHistorico() : setHistorico(true))} data-teste="historico-ptax-proposta">
            Histórico
          </button>
        )}
        {extra}
      </div>
      {aviso && (
        <p className="aviso aviso-alerta" role="status" data-teste="aviso-ptax-proposta">
          {aviso}
        </p>
      )}
      {editavel && divergentes.length > 1 && (
        <div className="aviso aviso-alerta escolha-ptax" data-teste="ptax-divergente">
          <strong>Esta proposta tem PTAX diferentes entre os itens em dólar. Escolha a PTAX da proposta para continuar.</strong>
          <div className="opcoes-ptax">
            {divergentes.map((x) => (
              <button key={x} type="button" className="botao-secundario" disabled={!podeManual} title={podeManual ? undefined : 'Exige a permissão “editar PTAX”'} onClick={() => setAlterar({ valor: x, escolhida: x })} data-teste={`escolher-ptax-${x}`}>
                Usar {ptax4(x)}
              </button>
            ))}
            <button type="button" className="botao-secundario" onClick={() => void consultar()}>
              Consultar a PTAX oficial de hoje
            </button>
          </div>
          <span className="texto-secundario">Nada é escolhido automaticamente. Até a escolha, a proposta não pode ser enviada.</span>
        </div>
      )}
      {alterar && (
        <ModalAlterarPtax
          v={v}
          inicial={alterar}
          aoFechar={() => setAlterar(null)}
          aoAplicar={(nova) => {
            aplicar(aplicarPtaxDaProposta(v, d, nova));
            setAlterar(null);
            setAviso('');
          }}
          eu={eu}
          podeManual={podeManual}
        />
      )}
      {historico && p && (
        <Modal
          titulo="Histórico da PTAX da proposta"
          aoFechar={() => setHistorico(false)}
          largo
          rodape={
            <button type="button" className="botao-secundario" onClick={() => setHistorico(false)}>
              Fechar
            </button>
          }
        >
          <ul className="linha-tempo" data-teste="lista-historico-ptax-proposta">
            {p.historico
              .slice()
              .reverse()
              .map((h, n) => (
                <li key={n}>
                  <strong>{dataBr(h.data.slice(0, 10))}{h.data.length > 10 ? ` às ${h.data.slice(11, 16)}` : ''}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
                </li>
              ))}
          </ul>
        </Modal>
      )}
    </section>
  );
}

/** Alterar (manual 🔒) ou aplicar a oficial consultada: prévia do efeito antes de aplicar a TODOS os itens em dólar. */
function ModalAlterarPtax({ v, inicial, aoFechar, aoAplicar, eu, podeManual }: { v: Versao; inicial: { valor?: number; oficial?: RespostaPtax['oficial']; escolhida?: number }; aoFechar: () => void; aoAplicar: (p: PtaxCotacao) => void; eu: string; podeManual: boolean }) {
  const { d, dados } = useProposta();
  const hoje = dados.opcoes.hoje;
  const oficial = inicial.oficial;
  const [valor, setValor] = useState(inicial.valor ? String(inicial.valor) : '');
  const [origem, setOrigem] = useState<OrigemPtaxManual | ''>(inicial.escolhida ? 'Ajuste manual' : '');
  const [dataRef, setDataRef] = useState(hoje);
  const [justificativa, setJustificativa] = useState(inicial.escolhida ? 'Escolhida entre as PTAX já usadas pelos itens em dólar (proposta com taxas divergentes)' : '');
  const n = Number(valor.replace(',', '.'));
  const m = { valor: n, origem: origem || undefined, dataReferencia: dataRef, justificativa };
  const erros = oficial ? [] : validarPtaxManual(m, hoje);
  const previa = n > 0 ? previaPtaxDaProposta(v, d, n) : null;
  const atual = v.ptaxProposta ?? novaPtax();
  const aplicar = () => {
    if (oficial) {
      const { manual: _m, ...resto } = atual;
      return aoAplicar({ ...resto, oficial, historico: [...atual.historico, { data: agora(), texto: `PTAX oficial aplicada: ${ptax4(oficial.valor)} (Banco Central, venda, fechamento de ${dataBr(oficial.dataReferencia)})`, autor: eu }] });
    }
    aoAplicar(substituirPtax(atual, { ...(m as PtaxManual), usuario: eu, alteradaEm: agora() }));
  };
  return (
    <Modal
      titulo={oficial ? 'Aplicar a PTAX oficial à proposta' : 'Alterar a PTAX da proposta'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!previa || erros.length > 0 || (!oficial && !podeManual)} onClick={aplicar} data-teste="aplicar-ptax-proposta">
            Aplicar a todos os itens em dólar
          </button>
        </>
      }
    >
      <div className="form-grade grade-4" data-teste="form-ptax-proposta">
        <label>
          PTAX atual
          <input value={ptax4(ptaxDaProposta(v))} readOnly />
        </label>
        <label>
          {oficial ? 'PTAX oficial' : 'Nova PTAX *'}
          <input type="number" min={0} step="0.0001" value={valor} readOnly={Boolean(oficial)} onChange={(e) => setValor(e.target.value)} data-teste="ptax-proposta-nova" />
        </label>
        {!oficial && (
          <>
            <label>
              Origem *
              <select value={origem} onChange={(e) => setOrigem(e.target.value as OrigemPtaxManual)} data-teste="ptax-proposta-origem">
                <option value="">Selecione…</option>
                {ORIGENS_PTAX.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <CampoData rotulo="Data de referência" valor={dataRef} aoMudar={setDataRef} max={hoje} teste="ptax-proposta-data" obrigatorio />
            <label className="largura-total">
              Justificativa *
              <textarea rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="ptax-proposta-justificativa" />
            </label>
          </>
        )}
      </div>
      {!oficial && erros.length > 0 && valor && (
        <ul className="aviso aviso-alerta" data-teste="erros-ptax-proposta">
          {erros.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {previa && (
        <>
          <h3>Prévia do efeito nesta proposta</h3>
          <div className="rolagem quadro-tabela">
            <table className="tabela" data-teste="previa-ptax-proposta">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="numero">Custo unit. antes</th>
                  <th className="numero">Custo unit. depois</th>
                  <th className="numero">Preço unit. antes</th>
                  <th className="numero">Preço unit. depois</th>
                  <th className="numero">Margem antes</th>
                  <th className="numero">Margem depois</th>
                  <th>O que acontece</th>
                </tr>
              </thead>
              <tbody>
                {previa.itens.map((x) => (
                  <tr key={x.id}>
                    <td>Item {x.n}</td>
                    <td className="numero nowrap">{moeda2(x.antes.custo)}</td>
                    <td className="numero nowrap">{moeda2(x.depois.custo)}</td>
                    <td className="numero nowrap">{moeda2(x.antes.preco)}</td>
                    <td className="numero nowrap">{moeda2(x.depois.preco)}</td>
                    {/* margemPct já vem em pontos percentuais (0–100): pctNum, não pct (que multiplica por 100) */}
                    <td className="numero nowrap">{pctNum(x.antes.margem)}</td>
                    <td className="numero nowrap">{pctNum(x.depois.margem)}</td>
                    <td>{x.precoFixo ? 'Preço fixo mantido; a margem efetiva muda' : 'Preço recalculado; a margem desejada é mantida'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="aviso aviso-info" data-teste="previa-ptax-totais">
            Total dos produtos {moeda2(previa.antes.total)} → {moeda2(previa.depois.total)} · Margem bruta {moeda2(previa.antes.bruta)} ({pct(previa.antes.percentual)}) → {moeda2(previa.depois.bruta)} ({pct(previa.depois.percentual)}). Itens em reais não mudam.
          </p>
        </>
      )}
    </Modal>
  );
}
